import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  StatusBar,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { BaseAdminScreen, AdminErrorBoundary } from '@/components';
import { GlassCard } from '@/components/shared/redesign/GlassCard';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';
import { SurveyResultsView } from '@/components/admin/corkBoard/survey/SurveyResultsView';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import {
  REDESIGN_SPACING,
  REDESIGN_TYPOGRAPHY,
} from '@/constants/residents/homeConstants';
import {
  useGetSurveyResponsesQuery,
  useGetSurveyResultsQuery,
} from '@/store/api/corkBoard/corkBoardApi';
import {
  useGetSurveySentimentQuery,
  usePostReclassifySurveyMutation,
} from '@/store/api/sentiment/sentimentApi';
import { SENTIMENT_POLL_INTERVAL_MS } from '@/constants/sentimentConstants';
import { SentimentBadge } from '@/components/admin/corkBoard/sentiment/SentimentBadge';
import { QuestionSentimentCard } from '@/components/admin/corkBoard/sentiment/QuestionSentimentCard';
import { computeAwaiting } from '@/components/admin/corkBoard/sentiment/computeAwaiting';
import type { QuestionResult, SurveyResponseItem } from '@/types/residents/corkBoard';
import type { SurveySentimentSnapshot } from '@/types/sentiment';
import { DesktopSurveyResponsesView } from './desktop/DesktopSurveyResponsesView';

type SurveyResponsesRouteProp = RouteProp<RootStackParamList, 'SurveyResponses'>;

export const SurveyResponsesScreen: React.FC = () => {
  const route = useRoute<SurveyResponsesRouteProp>();
  const { threadId } = route.params;
  const navigation = useNavigation();
  const { layoutMode } = useResponsive();
  const { colors } = useThemeColors();

  const isDesktop = layoutMode === 'desktop';

  if (isDesktop) {
    return <DesktopSurveyResponsesView threadId={threadId} />;
  }

  // Not a fullscreen route: the shared AdminHeaderWithMenu stays mounted across
  // the drill-in/out, so navigating back no longer remounts the header (which
  // caused the laggy header re-render). Mirrors the ResidentDetails pattern —
  // BaseAdminScreen relies on the navigator's outer GradientBackground and the
  // in-content ScreenBackLink provides the back affordance.
  return (
    <AdminErrorBoundary>
      <BaseAdminScreen
        screenName="SurveyResponses"
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <View style={styles.screen}>
          <StatusBar barStyle="light-content" />
          <View style={styles.header}>
            <ScreenBackLink onPress={() => navigation.goBack()} />
            <View style={styles.headerTitleSpacer}>
              <Text
                style={[
                  REDESIGN_TYPOGRAPHY.sectionTitle,
                  { color: colors.text.primary },
                ]}
                numberOfLines={1}
              >
                Survey responses
              </Text>
            </View>
            <View style={styles.headerRightSpacer} />
          </View>
          <SurveyResponsesList threadId={threadId} />
        </View>
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

function hasPendingSentiment(snap: SurveySentimentSnapshot | undefined): boolean {
  if (!snap) return true;
  return snap.answers.some((a) => a.status === 'pending') ||
    snap.questions.some((q) => q.status === 'pending');
}

const SurveyResponsesList: React.FC<{ threadId: string }> = ({ threadId }) => {
  const { colors } = useThemeColors();
  const [page, setPage] = useState(0);
  const numericThreadId = Number(threadId);

  const { data: results } = useGetSurveyResultsQuery(threadId);
  const { data, isLoading } = useGetSurveyResponsesQuery({ threadId, page, pageSize: 20 });

  // pollingInterval reads from a ref so we don't trigger a hook-restart loop every
  // render. The ref is synced to the latest snapshot via useEffect (post-render).
  const sentimentRef = useRef<SurveySentimentSnapshot | undefined>(undefined);
  const { data: sentiment } = useGetSurveySentimentQuery(numericThreadId, {
    pollingInterval: hasPendingSentiment(sentimentRef.current)
      ? SENTIMENT_POLL_INTERVAL_MS
      : 0,
  });
  useEffect(() => {
    sentimentRef.current = sentiment;
  }, [sentiment]);

  const [postReclassify] = usePostReclassifySurveyMutation();

  const questionsById = useMemo(() => {
    const map = new Map<number, QuestionResult>();
    results?.questions.forEach((q) => map.set(q.questionId, q));
    return map;
  }, [results]);

  const longTextQuestions = useMemo(
    () => (results?.questions ?? []).filter((q) => q.type === 'long_text'),
    [results],
  );

  if (isLoading || !data || !results) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator color={colors.newsfeed.tagActiveText} />
      </View>
    );
  }

  const allResponses = data.responses;

  const sentimentHeader =
    longTextQuestions.length > 0 ? (
      <View>
        {longTextQuestions.map((q) => (
          <QuestionSentimentCard
            key={q.questionId}
            questionId={q.questionId}
            question={sentiment?.questions.find((s) => s.questionId === q.questionId)}
            awaitingAnalysisCount={computeAwaiting(q.questionId, allResponses, sentiment)}
            onReclassify={() =>
              postReclassify({ threadId: numericThreadId, scope: 'question', questionId: q.questionId })
            }
          />
        ))}
      </View>
    ) : null;

  return (
    <FlatList
      contentContainerStyle={styles.list}
      data={allResponses}
      keyExtractor={(r) => String(r.responseId)}
      ListHeaderComponent={
        <View>
          {/* Aggregate analysis (poll/rating bars) — admin-only, from the
              "Survey Analysis — Admin Only" design. Rendered at the top. */}
          <SurveyResultsView results={results} />
          {/* AI sentiment summary cards, below the quantitative aggregates and
              above the individual-response list. LAYOUT CHOICE made during the
              sentiment<->admin-survey rebase; pending the returning engineer's
              review — see PR #148. */}
          {sentimentHeader}
          <Text
            style={[
              REDESIGN_TYPOGRAPHY.caption,
              styles.listHeader,
              { color: colors.text.secondary },
            ]}
          >
            {data.pagination.totalCount} response{data.pagination.totalCount === 1 ? '' : 's'}
          </Text>
        </View>
      }
      ListEmptyComponent={
        <View style={styles.empty}>
          <Text style={[REDESIGN_TYPOGRAPHY.body, { color: colors.text.secondary }]}>
            No responses yet.
          </Text>
        </View>
      }
      renderItem={({ item }) => (
        <ResponseCard
          response={item}
          questionsById={questionsById}
          sentiment={sentiment}
        />
      )}
      onEndReached={() => {
        if (page + 1 < data.pagination.totalPages) setPage((p) => p + 1);
      }}
      onEndReachedThreshold={0.4}
    />
  );
};

const ResponseCard: React.FC<{
  response: SurveyResponseItem;
  questionsById: Map<number, QuestionResult>;
  sentiment: SurveySentimentSnapshot | undefined;
}> = ({ response, questionsById, sentiment }) => {
  const { colors } = useThemeColors();
  const isAnonymous = !response.account;
  return (
    <GlassCard
      variant="soft"
      borderRadius={REDESIGN_SPACING.cardBorderRadius}
      padding={REDESIGN_SPACING.cardPadding}
      style={styles.cardSpacing}
    >
      <View style={styles.metaRow}>
        <Text style={[REDESIGN_TYPOGRAPHY.cardTitle, { color: colors.text.primary }]}>
          {isAnonymous ? 'Anonymous' : response.account!.name}
        </Text>
        <Text style={[REDESIGN_TYPOGRAPHY.small, { color: colors.text.secondary }]}>
          {new Date(response.submittedAt).toLocaleString()}
        </Text>
      </View>
      {response.answers.map((a) => {
        const q = questionsById.get(a.questionId);
        if (!q) return null;
        return (
          <View key={a.questionId} style={styles.answerRow}>
            <Text style={[REDESIGN_TYPOGRAPHY.label, { color: colors.text.secondary }]}>
              {q.prompt}
            </Text>
            {renderAnswer(q, a, sentiment)}
          </View>
        );
      })}
    </GlassCard>
  );
};

function renderAnswer(
  q: QuestionResult,
  a: SurveyResponseItem['answers'][number],
  sentiment: SurveySentimentSnapshot | undefined,
): React.ReactElement {
  if (q.type === 'poll' && a.optionId !== undefined) {
    const label = q.pollAggregate?.find((p) => p.optionId === a.optionId)?.label ?? `Option ${a.optionId}`;
    return <Text style={styles.answerText}>{label}</Text>;
  }
  if (q.type === 'rating' && a.ratingValue !== undefined) {
    return <Text style={styles.answerText}>{`Rating: ${a.ratingValue}`}</Text>;
  }
  if (q.type === 'long_text' && a.textValue !== undefined && a.textValue !== null) {
    const sentForAnswer =
      a.id != null
        ? sentiment?.answers.find((s) => s.answerId === a.id)
        : undefined;
    return (
      <View style={styles.textWithBadge}>
        <Text style={[styles.answerText, styles.textValueFlex]}>{a.textValue}</Text>
        {sentForAnswer && (
          <SentimentBadge
            status={sentForAnswer.status}
            label={sentForAnswer.label}
            confidence={sentForAnswer.confidence}
            errorReason={sentForAnswer.errorReason}
          />
        )}
      </View>
    );
  }
  return <Text style={styles.answerText}>{'—'}</Text>;
}

const styles = StyleSheet.create({
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  screen: { flex: 1, backgroundColor: 'transparent' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 14,
    gap: 8,
  },
  headerTitleSpacer: { flex: 1, alignItems: 'center' },
  headerRightSpacer: { width: 36 },
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  list: {
    padding: REDESIGN_SPACING.horizontalPadding,
    paddingTop: REDESIGN_SPACING.sectionGap,
    gap: REDESIGN_SPACING.sectionGap,
  },
  listHeader: { paddingBottom: REDESIGN_SPACING.sectionGap },
  cardSpacing: { marginBottom: REDESIGN_SPACING.sectionGap },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: REDESIGN_SPACING.sectionGap,
    gap: 8,
  },
  answerRow: { gap: 2, marginVertical: 4 },
  answerText: {},
  textWithBadge: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  textValueFlex: { flex: 1 },
  empty: { paddingTop: 32, alignItems: 'center' },
});
